import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { legalRequirements, getComplianceStatus } from '../data/complianceData';

export const ComplianceEngine = () => {
  const [completedChecks, setCompletedChecks] = useState([]);
  const [isAuditMode, setIsAuditMode] = useState(false);
  
  // Sikker sjekk for å unngå krasj hvis data er undefined
  const [activeSection, setActiveSection] = useState(
    legalRequirements && legalRequirements.length > 0 ? legalRequirements[0].id : null
  );

  const calculateScore = useMemo(() => {
    if (!legalRequirements) return 0;
    let totalWeight = 0;
    let achievedWeight = 0;

    legalRequirements.forEach(section => {
      section.checks.forEach(check => {
        totalWeight += check.weight;
        if (completedChecks.includes(check.id)) {
          achievedWeight += check.weight;
        }
      });
    });

    return totalWeight > 0 ? Math.round((achievedWeight / totalWeight) * 100) : 0;
  }, [completedChecks]);

  const toggleCheck = (id) => {
    setCompletedChecks(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const status = getComplianceStatus(calculateScore) || { label: 'Ukjent', color: 'slate', icon: 'HelpCircle' };

  if (!activeSection) {
    return <div className="p-10 text-center text-slate-500 text-sm">Ingen compliance-data tilgjengelig.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Compliance Engine</h1>
          <p className="text-slate-500 mt-1">Sikre at virksomheten oppfyller Arbeidsmiljølovens krav.</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant={isAuditMode ? "primary" : "secondary"} 
            onClick={() => setIsAuditMode(!isAuditMode)}
            icon={<SafeIcon name="Eye" />}
          >
            {isAuditMode ? "Lukk Audit Mode" : "Aktiver Audit Mode"}
          </Button>
          <Button variant="primary" icon={<SafeIcon name="Download" />}>
            Generer Compliance-rapport
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <Card className={`border-t-4`} style={{ borderTopColor: `var(--${status.color}-500)` }}>
            <CardContent className="p-6 text-center">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Total Compliance Score</p>
              <div className={`text-5xl font-black text-${status.color}-600 mb-2`}>{calculateScore}%</div>
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-${status.color}-50 text-${status.color}-700 text-xs font-bold`}>
                <SafeIcon name={status.icon} />
                {status.label}
              </div>
              
              <div className="mt-8 space-y-4 text-left">
                {legalRequirements.map(section => {
                  const sectionChecks = section.checks.map(c => c.id);
                  const completedInSection = completedChecks.filter(id => sectionChecks.includes(id)).length;
                  const progress = Math.round((completedInSection / section.checks.length) * 100);

                  return (
                    <div key={section.id} className="space-y-1">
                      <div className="flex justify-between text-[10px] font-bold text-slate-500 uppercase">
                        <span>{section.section}</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-500 ${progress === 100 ? 'bg-emerald-500' : 'bg-indigo-600'}`} 
                          style={{ width: `${progress}%` }} 
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {isAuditMode && (
            <Card className="bg-slate-900 border-none text-white">
              <CardHeader title="Audit Log" subtitle="Dokumentasjonshistorikk" icon={<SafeIcon name="Search" className="text-indigo-400" />} />
              <CardContent className="space-y-4">
                {[
                  { date: '12.04', action: 'Kartlegging lagt til §4-3', user: 'JD' },
                  { date: '10.04', action: 'HMS-plan godkjent §3-1', user: 'JD' },
                ].map((log, i) => (
                  <div key={i} className="flex gap-3 text-xs border-l border-slate-700 pl-4 py-1">
                    <span className="text-slate-500 font-mono">{log.date}</span>
                    <span className="flex-1 text-slate-300">{log.action}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="flex bg-white p-1 rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
            {legalRequirements.map(section => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex-1 min-w-[120px] px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                  activeSection === section.id ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:text-indigo-600'
                }`}
              >
                {section.section}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {legalRequirements.map(section => section.id === activeSection && (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="bg-white p-6 rounded-2xl border border-slate-200">
                  <h3 className="text-xl font-bold text-slate-900">{section.title}</h3>
                  <p className="text-sm text-slate-500 mt-1">{section.description}</p>
                </div>

                {section.checks.map(check => (
                  <Card 
                    key={check.id} 
                    onClick={() => toggleCheck(check.id)}
                    className={`cursor-pointer transition-all ${
                      completedChecks.includes(check.id) ? 'border-indigo-200 bg-indigo-50/30' : 'hover:border-indigo-200'
                    }`}
                  >
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors ${
                          completedChecks.includes(check.id) ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-200 bg-white'
                        }`}>
                          {completedChecks.includes(check.id) && <SafeIcon name="Check" size={14} />}
                        </div>
                        <span className={`text-sm font-medium ${completedChecks.includes(check.id) ? 'text-slate-900' : 'text-slate-600'}`}>
                          {check.label}
                        </span>
                      </div>
                      
                      {isAuditMode && (
                        <div className="flex items-center gap-2">
                          <button className="p-2 text-slate-400 hover:text-indigo-600" onClick={(e) => e.stopPropagation()}>
                            <SafeIcon name="Paperclip" size={16} />
                          </button>
                          <button className="p-2 text-slate-400 hover:text-indigo-600" onClick={(e) => e.stopPropagation()}>
                            <SafeIcon name="MessageSquare" size={16} />
                          </button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {section.checks.some(c => !completedChecks.includes(c.id)) && (
                  <Card className="bg-rose-50 border-rose-100">
                    <CardContent className="p-4 flex items-start gap-3">
                      <SafeIcon name="Info" className="text-rose-600 mt-1" />
                      <div>
                        <p className="text-xs font-bold text-rose-900">Anbefalte tiltak for {section.section}</p>
                        <p className="text-[10px] text-rose-700 mt-1 leading-relaxed">
                          For å oppfylle kravene i denne paragrafen bør du snarest legge inn dokumentasjon på 
                          gjennomførte vernerunder og oppdatere handlingsplanen.
                        </p>
                        <Button variant="danger" size="sm" className="mt-3 text-[10px]">
                          Opprett oppgave
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};